import { useMisPageAudit, useMisFilterAudit } from '../api/useMisPageAudit';
import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { 
  uploadICICIBankStatementReco, 
  uploadICICITallyDataReco, 
  runICICIBankTallyReconciliation 
} from "../api/misApis";
import { 
  UploadCloud, 
  CheckCircle2, 
  XCircle, 
  AlertTriangle, 
  RefreshCw, 
  CalendarDays,
  FileSpreadsheet,
  FileText,
  Building2,
  Database,
  ArrowLeft
} from "lucide-react";
import "../Assets/Css/alert.css"; 

function ICICIBankTallyReconciliation() {
  useMisPageAudit('icici-bank-tally-reco');
  const navigate = useNavigate();
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");
  
  const [bankFile, setBankFile] = useState(null);
  const [tallyFile, setTallyFile] = useState(null);
  
  const bankInputRef = useRef(null);
  const tallyInputRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [alertMsg, setAlertMsg] = useState("");
  const [alertType, setAlertType] = useState("");

  const [recoData, setRecoData] = useState([]);
  const [budgetData, setBudgetData] = useState([]);
  const [summary, setSummary] = useState({
    totalRecords: 0,
    matchedRecords: 0,
    unmatchedRecords: 0,
    amountMismatches: 0,
  });

  const selectedPeriod = month && year ? `${year}-${month}` : "";
  useMisFilterAudit('icici-bank-tally-reco', { month: selectedPeriod });

  const showAlert = (msg, type) => {
    setAlertMsg(msg);
    setAlertType(type);
    setTimeout(() => {
      setAlertMsg("");
    }, 4000);
  };

  const handleBankUpload = async () => {
    if (!selectedPeriod) {
      showAlert("Month and Year are mandatory before upload", "error");
      return;
    }
    if (!bankFile) {
      showAlert("Please select a Bank Statement file", "error");
      return;
    }

    setLoading(true);
    try {
      const res = await uploadICICIBankStatementReco(bankFile, selectedPeriod);
      showAlert(res.data.message || "Bank statement uploaded", "success");
      setBankFile(null);
      if (bankInputRef.current) bankInputRef.current.value = "";
    } catch (err) {
      console.error(err);
      showAlert(err.response?.data?.error || "Error uploading bank statement", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleTallyUpload = async () => {
    if (!selectedPeriod) {
      showAlert("Month and Year are mandatory before upload", "error");
      return;
    }
    if (!tallyFile) {
      showAlert("Please select a Tally file", "error");
      return;
    }

    setLoading(true);
    try {
      const res = await uploadICICITallyDataReco(tallyFile, selectedPeriod);
      showAlert(res.data.message || "Tally data uploaded", "success");
      setTallyFile(null);
      if (tallyInputRef.current) tallyInputRef.current.value = "";
    } catch (err) {
      console.error(err);
      showAlert(err.response?.data?.error || "Error uploading Tally data", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchReconciliationData = async () => {
    if (!selectedPeriod) return;
    setLoading(true);
    try {
      const res = await runICICIBankTallyReconciliation(selectedPeriod);
      setRecoData(res.data.data || []);
      setBudgetData(res.data.budgetData || []);
      if (res.data.summary) {
        setSummary(res.data.summary);
      }
    } catch (err) {
      console.error(err);
      showAlert("Failed to fetch reconciliation data", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full h-full min-h-screen p-8 bg-gradient-to-br from-gray-50 to-blue-50 text-gray-800 font-sans flex flex-col">
      {/* ALERTS */}
      {alertMsg && (
        <div className={`fixed top-5 right-5 z-50 px-6 py-3 rounded shadow-lg flex items-center gap-3 animate-bounce font-medium text-white ${alertType === "success" ? "bg-green-600" : "bg-red-600"}`}>
          {alertType === "success" ? <CheckCircle2 size={20} /> : <AlertTriangle size={20} />}
          {alertMsg}
        </div>
      )}

      {/* HEADER */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <button 
            onClick={() => navigate(-1)} 
            className="p-2 bg-white text-gray-600 rounded-full shadow-sm border border-gray-200 hover:bg-gray-50 hover:text-gray-900 transition-colors cursor-pointer"
            title="Go Back"
          >
            <ArrowLeft size={24} />
          </button>
          <div>
            <h2 className="text-3xl font-extrabold text-gray-900 tracking-tight flex items-center gap-3">
              <Building2 className="text-blue-600" size={32} />
              ICICI Bank vs Tally Reconciliation
            </h2>
            <p className="text-gray-500 mt-2 font-medium">Automated data cleaning, mapping, and variance analysis.</p>
          </div>
        </div>
      </div>

      {/* CONTROLS (Glassmorphism Card) */}
      <div className="bg-white/80 backdrop-blur-md border border-white p-6 shadow-sm rounded-2xl flex flex-wrap items-end gap-6 mb-8 transition hover:shadow-md">
        <div className="flex flex-col flex-1 min-w-[200px]">
          <label className="font-semibold text-gray-700 mb-2 flex items-center gap-2">
            <CalendarDays size={18} className="text-blue-500"/> Year
          </label>
          <select 
            value={year} 
            onChange={(e) => setYear(e.target.value)}
            className="w-full border-gray-300 rounded-xl p-3 bg-gray-50/50 hover:bg-gray-50 transition focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer shadow-sm"
          >
            <option value="">-- Select Year --</option>
            {[2025, 2026, 2027, 2028, 2029, 2030].map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>

        <div className="flex flex-col flex-1 min-w-[200px]">
          <label className="font-semibold text-gray-700 mb-2 flex items-center gap-2">
            <CalendarDays size={18} className="text-blue-500"/> Month
          </label>
          <select 
            value={month} 
            onChange={(e) => setMonth(e.target.value)}
            className="w-full border-gray-300 rounded-xl p-3 bg-gray-50/50 hover:bg-gray-50 transition focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer shadow-sm"
          >
            <option value="">-- Select Month --</option>
            {["01","02","03","04","05","06","07","08","09","10","11","12"].map((m, idx) => (
              <option key={m} value={m}>{new Date(0, idx).toLocaleString('default', { month: 'long' })} ({m})</option>
            ))}
          </select>
        </div>
        
        <div className="flex flex-col flex-none">
           <button 
             onClick={fetchReconciliationData}
             disabled={!selectedPeriod || loading}
             className="flex items-center justify-center gap-2 bg-gradient-to-r from-gray-800 to-gray-900 text-white font-semibold py-3 px-8 rounded-xl shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:hover:translate-y-0 cursor-pointer"
           >
             <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
             {loading ? "Running..." : "Run Reconciliation"}
           </button>
        </div>
      </div>

      {/* UPLOADS */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
        {/* Bank Upload Card */}
        <div className="group bg-white p-6 shadow-sm hover:shadow-xl rounded-2xl border border-gray-100 transition-all duration-300 flex flex-col justify-between relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-400 to-blue-600"></div>
          <div>
            <div className="flex items-center gap-3 mb-3">
              <div className="p-3 bg-blue-100 text-blue-600 rounded-xl">
                <Building2 size={24} />
              </div>
              <h3 className="font-extrabold text-xl text-gray-800">Bank Statement</h3>
            </div>
            <p className="text-gray-500 text-sm mb-6 leading-relaxed">
              Upload the downloaded bank statement. The system automatically standardizes dates, amounts, and removes unnecessary summary rows.
            </p>
            
            <label className="block w-full cursor-pointer">
              <span className="sr-only">Choose file</span>
              <input 
                type="file" 
                accept=".xlsx,.xls,.csv" 
                ref={bankInputRef}
                onChange={(e) => setBankFile(e.target.files[0])}
                disabled={!selectedPeriod}
                className="block w-full text-sm text-gray-500 
                  file:mr-4 file:py-2.5 file:px-6 
                  file:rounded-full file:border-0 
                  file:text-sm file:font-bold 
                  file:bg-blue-50 file:text-blue-600 
                  hover:file:bg-blue-100 
                  file:cursor-pointer
                  disabled:opacity-50 disabled:cursor-not-allowed
                  transition-colors"
              />
            </label>
          </div>
          <button 
            onClick={handleBankUpload}
            disabled={!selectedPeriod || loading || !bankFile}
            className="mt-6 w-full flex items-center justify-center gap-2 bg-blue-600 text-white font-bold py-3 px-4 rounded-xl shadow-md hover:bg-blue-700 hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <UploadCloud size={20} />
            {loading ? "Processing..." : "Process Bank Data"}
          </button>
        </div>

        {/* Tally Upload Card */}
        <div className="group bg-white p-6 shadow-sm hover:shadow-xl rounded-2xl border border-gray-100 transition-all duration-300 flex flex-col justify-between relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-orange-400 to-red-500"></div>
          <div>
            <div className="flex items-center gap-3 mb-3">
              <div className="p-3 bg-orange-100 text-orange-600 rounded-xl">
                <Database size={24} />
              </div>
              <h3 className="font-extrabold text-xl text-gray-800">Tally Data</h3>
            </div>
            <p className="text-gray-500 text-sm mb-6 leading-relaxed">
              Upload the Tally export for the selected month and year. This will be mapped against the cleaned bank statement data.
            </p>
            
            <label className="block w-full cursor-pointer">
              <span className="sr-only">Choose file</span>
              <input 
                type="file" 
                accept=".xlsx,.xls,.csv" 
                ref={tallyInputRef}
                onChange={(e) => setTallyFile(e.target.files[0])}
                disabled={!selectedPeriod}
                className="block w-full text-sm text-gray-500 
                  file:mr-4 file:py-2.5 file:px-6 
                  file:rounded-full file:border-0 
                  file:text-sm file:font-bold 
                  file:bg-orange-50 file:text-orange-600 
                  hover:file:bg-orange-100 
                  file:cursor-pointer
                  disabled:opacity-50 disabled:cursor-not-allowed
                  transition-colors"
              />
            </label>
          </div>
          <button 
            onClick={handleTallyUpload}
            disabled={!selectedPeriod || loading || !tallyFile}
            className="mt-6 w-full flex items-center justify-center gap-2 bg-gradient-to-r from-orange-500 to-red-500 text-white font-bold py-3 px-4 rounded-xl shadow-md hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <UploadCloud size={20} />
            {loading ? "Processing..." : "Process Tally Data"}
          </button>
        </div>
      </div>

      {/* SUMMARY CARDS */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white shadow-sm border border-gray-100 p-5 rounded-2xl flex items-center justify-between hover:-translate-y-1 transition-transform duration-300">
          <div>
            <p className="text-gray-500 text-xs font-bold uppercase tracking-wider mb-1">Total Records</p>
            <p className="text-3xl font-black text-gray-800">{summary.totalRecords}</p>
          </div>
          <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center text-gray-600">
            <FileSpreadsheet size={24}/>
          </div>
        </div>
        
        <div className="bg-white shadow-sm border border-gray-100 p-5 rounded-2xl flex items-center justify-between hover:-translate-y-1 transition-transform duration-300">
          <div>
            <p className="text-green-600/80 text-xs font-bold uppercase tracking-wider mb-1">Matched</p>
            <p className="text-3xl font-black text-green-600">{summary.matchedRecords}</p>
          </div>
          <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center text-green-600">
            <CheckCircle2 size={24}/>
          </div>
        </div>
        
        <div className="bg-white shadow-sm border border-gray-100 p-5 rounded-2xl flex items-center justify-between hover:-translate-y-1 transition-transform duration-300">
          <div>
            <p className="text-red-500/80 text-xs font-bold uppercase tracking-wider mb-1">Unmatched</p>
            <p className="text-3xl font-black text-red-500">{summary.unmatchedRecords}</p>
          </div>
          <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center text-red-500">
            <XCircle size={24}/>
          </div>
        </div>
        
        <div className="bg-white shadow-sm border border-gray-100 p-5 rounded-2xl flex items-center justify-between hover:-translate-y-1 transition-transform duration-300">
          <div>
            <p className="text-orange-500/80 text-xs font-bold uppercase tracking-wider mb-1">Mismatches</p>
            <p className="text-3xl font-black text-orange-500">{summary.amountMismatches}</p>
          </div>
          <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center text-orange-500">
            <AlertTriangle size={24}/>
          </div>
        </div>
      </div>

      {/* DATA TABLE */}
      <div className="flex-1 bg-white shadow-lg rounded-2xl border border-gray-200 overflow-hidden flex flex-col">
        <div className="bg-gray-50 border-b border-gray-200 px-6 py-4 flex items-center gap-3">
          <FileText className="text-gray-500" size={20}/>
          <h3 className="font-bold text-gray-800 text-lg">Reconciliation Results</h3>
        </div>
        <div className="overflow-x-auto h-[500px]">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-white sticky top-0 z-10 shadow-sm">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap">Party Name</th>
                <th className="px-6 py-4 text-center text-xs font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap">Month</th>
                <th className="px-6 py-4 text-center text-xs font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap">Year</th>
                <th className="px-6 py-4 text-right text-xs font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap">Statement Amount</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap">Tally Name</th>
                <th className="px-6 py-4 text-right text-xs font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap">Tally Amount</th>
                <th className="px-6 py-4 text-center text-xs font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap">Match Status</th>
                <th className="px-6 py-4 text-right text-xs font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap">Difference Amount</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100 overflow-y-auto">
              {recoData.length > 0 ? (
                recoData.map((row, idx) => {
                  const status = (row.MatchStatus || "").toUpperCase();
                  const isMatch = status === "MATCH";
                  const isMismatch = status.includes("AMOUNT DIFFERENCE");

                  return (
                    <tr key={idx} className="hover:bg-gray-50/80 transition-colors group">
                      <td className="px-6 py-3 text-sm text-gray-800" title={row.PartyName}>
                        {row.PartyName || '-'}
                      </td>
                      <td className="px-6 py-3 text-center whitespace-nowrap text-sm text-gray-600 font-medium">
                        {row.Month || '-'}
                      </td>
                      <td className="px-6 py-3 text-center whitespace-nowrap text-sm text-gray-600 font-medium">
                        {row.Year || '-'}
                      </td>
                      <td className="px-6 py-3 text-right font-bold text-sm text-blue-700">
                        ₹ {parseFloat(row.StatementAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="px-6 py-3 text-sm text-gray-800" title={row.TallyName}>
                        {row.TallyName || '-'}
                      </td>
                      <td className="px-6 py-3 text-right font-bold text-sm text-orange-700">
                        ₹ {parseFloat(row.TallyAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="px-6 py-3 text-center whitespace-nowrap">
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full font-bold text-xs shadow-sm ${isMatch ? 'bg-green-100 text-green-700 border border-green-200' : isMismatch ? 'bg-orange-100 text-orange-700 border border-orange-200' : 'bg-red-100 text-red-700 border border-red-200'}`}>
                          {isMatch ? <CheckCircle2 size={14}/> : isMismatch ? <AlertTriangle size={14} /> : <XCircle size={14}/>}
                          {status}
                        </span>
                      </td>
                      <td className="px-6 py-3 text-right font-bold text-sm text-gray-900">
                         ₹ {parseFloat(row.DifferenceAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </td>
                    </tr>
                  )
                })
              ) : (
                <tr>
                  <td colSpan="7" className="px-6 py-16 text-center text-gray-400">
                    <div className="flex flex-col items-center justify-center">
                      <FileSpreadsheet size={48} className="mb-4 text-gray-300" />
                      <p className="text-lg font-medium text-gray-500">No Data Available</p>
                      <p className="text-sm mt-1">Select a period and process files to see the reconciliation results.</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ICICI CREDIT CARD BUDGET VS ACTUAL TABLE */}
      <div className="flex-1 bg-white shadow-lg rounded-2xl border border-gray-200 overflow-hidden flex flex-col mt-8">
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200 px-6 py-4 flex items-center gap-3">
          <Database className="text-indigo-600" size={20}/>
          <h3 className="font-bold text-gray-800 text-lg">ICICI Credit Card Budget vs Actual</h3>
        </div>
        <div className="overflow-x-auto h-[400px]">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-white sticky top-0 z-10 shadow-sm">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap">Cost Element Name</th>
                <th className="px-6 py-4 text-center text-xs font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap">Month</th>
                <th className="px-6 py-4 text-center text-xs font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap">Year</th>
                <th className="px-6 py-4 text-right text-xs font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap">Actual Amount</th>
                <th className="px-6 py-4 text-right text-xs font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap">Budget Amount</th>
                <th className="px-6 py-4 text-right text-xs font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap">Difference (Budget - Actual)</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100 overflow-y-auto">
              {budgetData.length > 0 ? (
                budgetData.map((row, idx) => {
                  const actual = parseFloat(row.ActualAmount || 0);
                  const budget = parseFloat(row.BudgetMonthAmount || 0);
                  const diff = budget - actual;
                  const isNegative = diff < 0;

                  return (
                    <tr key={idx} className="hover:bg-gray-50/80 transition-colors group">
                      <td className="px-6 py-3 text-sm font-semibold text-gray-800 max-w-xs truncate" title={row["COST ELEMENT NAME"]}>
                        {row["COST ELEMENT NAME"] || '-'}
                      </td>
                      <td className="px-6 py-3 text-center whitespace-nowrap text-sm text-gray-600 font-medium">
                        {row.Month || '-'}
                      </td>
                      <td className="px-6 py-3 text-center whitespace-nowrap text-sm text-gray-600 font-medium">
                        {row.Year || '-'}
                      </td>
                      <td className="px-6 py-3 text-right font-bold text-sm text-orange-600">
                        ₹ {actual.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="px-6 py-3 text-right font-bold text-sm text-blue-700">
                        ₹ {budget.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </td>
                      <td className={`px-6 py-3 text-right font-bold text-sm ${isNegative ? 'text-red-600' : 'text-green-600'}`}>
                         ₹ {diff.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </td>
                    </tr>
                  )
                })
              ) : (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-gray-400">
                    <div className="flex flex-col items-center justify-center">
                      <FileSpreadsheet size={40} className="mb-4 text-gray-300" />
                      <p className="text-base font-medium text-gray-500">No Budget Data Found</p>
                      <p className="text-xs mt-1">Run the reconciliation to view budget details.</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default ICICIBankTallyReconciliation;
